import { ErrorMessage } from '@/components/common/ErrorMessage';
import { SCREEN } from '@/components/common/surfaces';
import { DetectionProgress } from '@/components/documents/DetectionProgress';
import { UploadCard } from '@/components/documents/UploadCard';
import { useCatalog } from '@/hooks/useCatalog';
import { useUploadFlow } from '@/hooks/useUploadFlow';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `/` — the prototype's "Nová kontrola dokumentu": upload a PDF and start the run. There is no
 * list of documents the server holds: a reviewed document is dropped when the user comes back here.
 *
 * While a run is in progress the whole screen is the progress card, as in the prototype.
 */
export const Upload: FC = () => {
  const { t } = useTranslation();
  const flow = useUploadFlow();
  const { categories } = useCatalog();

  if (flow.phase === 'detecting' && flow.document) {
    return (
      <DetectionProgress
        document={flow.document}
        categoryCount={categories.length}
        status={flow.detection}
        error={flow.pollingError}
      />
    );
  }

  return (
    <div className={`${SCREEN} justify-center px-7 pb-[72px] pt-11`}>
      <div className="w-full max-w-[720px]">
        <h1 className="m-0 mb-[7px] text-[23px] font-bold text-primary">{t('documents.title')}</h1>
        <p className="m-0 mb-[26px] max-w-[600px] text-[13.5px] leading-[1.6] text-muted">{t('documents.subtitle')}</p>
        {flow.error ? (
          <div className="mb-4">
            <ErrorMessage message={flow.error} />
          </div>
        ) : null}
        {flow.phase === 'checking' ? (
          <span className="sr-only" role="status">
            {t('common.loading')}
          </span>
        ) : (
          <UploadCard
            document={flow.document}
            outcome={flow.outcome}
            isStarting={flow.isStarting}
            categories={categories}
            onUploaded={flow.onUploaded}
            onStart={flow.startDetection}
          />
        )}
      </div>
    </div>
  );
};
